// ═══════════════════════════════════════════════════════════════════
//  RefsLayer — cross-document reference polylines on the Minimap.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase C of MINIMAP_ATLAS_PLAN. Walks atlas.crossDocumentRefs and
//  draws a polyline between two on-screen doc rectangles when both
//  are visible. Color-coded by ref.type. Bidirectional refs get
//  arrows on both ends.
//
//  Off-screen edges are culled — both endpoints must be inside the
//  viewport for the polyline to render.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import type { BlockGroup, WorkflowBlock, BlockDimensions } from '@/types/workflow';
import type { Atlas, CrossDocumentRef } from '@/types/atlas';

export interface RefsLayerProps {
  groups: BlockGroup[];
  blocks: WorkflowBlock[];
  blockDimensions?: Record<string, BlockDimensions>;
  toScreenX: (x: number) => number;
  toScreenY: (y: number) => number;
  toScreenSize: (s: number) => number;
  scale: number;
  atlas: Atlas;
  /** SVG canvas dimensions for viewport-cull comparison. */
  width: number;
  height: number;
  onRefClick?: (refId: string) => void;
}

interface DocRect {
  documentId: string;
  cx: number; // center x (minimap-pixel)
  cy: number; // center y
}

const REF_COLORS: Record<string, string> = {
  uses: 'hsl(215 16% 47%)',          // slate-500
  extends: 'hsl(25 95% 53%)',        // orange-500
  'depends-on': 'hsl(38 92% 50%)',   // amber-500
  'sheet-link': 'hsl(271 91% 65%)',  // purple-500
  other: 'hsl(215 14% 60%)',
};

export function RefsLayer({
  groups,
  blocks,
  blockDimensions,
  toScreenX,
  toScreenY,
  toScreenSize,
  scale,
  atlas,
  width,
  height,
  onRefClick,
}: RefsLayerProps) {
  // Map docId → center on minimap (only for atlasRef groups currently visible).
  const centers = useMemo<Map<string, DocRect>>(() => {
    const out = new Map<string, DocRect>();
    for (const g of groups) {
      if (!g.atlasRef) continue;
      const groupBlocks = blocks.filter((b) => g.blockIds.includes(b.id));
      if (groupBlocks.length === 0) continue;
      const bounds = computeBounds(groupBlocks, blockDimensions);
      if (!bounds) continue;
      const left = toScreenX(bounds.minX - 16) * scale;
      const top = toScreenY(bounds.minY - 32) * scale;
      const w = toScreenSize(bounds.maxX - bounds.minX + 32) * scale;
      const h = toScreenSize(bounds.maxY - bounds.minY + 48) * scale;
      out.set(g.atlasRef.documentId, {
        documentId: g.atlasRef.documentId,
        cx: left + w / 2,
        cy: top + h / 2,
      });
    }
    return out;
  }, [groups, blocks, blockDimensions, scale, toScreenX, toScreenY, toScreenSize]);

  const visibleEdges = useMemo(() => {
    return (atlas.crossDocumentRefs ?? []).flatMap<{ ref: CrossDocumentRef; from: DocRect; to: DocRect }>(
      (ref) => {
        const from = centers.get(ref.fromDocumentId);
        const to = centers.get(ref.toDocumentId);
        if (!from || !to) return [];
        if (!inViewport(from, width, height) || !inViewport(to, width, height)) return [];
        return [{ ref, from, to }];
      },
    );
  }, [atlas.crossDocumentRefs, centers, width, height]);

  if (visibleEdges.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
      data-fw-minimap-refs="true"
    >
      <defs>
        {Object.entries(REF_COLORS).map(([k, color]) => (
          <marker
            key={k}
            id={`ref-arrow-${k}`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
          </marker>
        ))}
      </defs>
      {visibleEdges.map(({ ref, from, to }) => {
        const colorKey = ref.type ?? 'other';
        const color = REF_COLORS[colorKey] ?? REF_COLORS.other;
        return (
          <g key={ref.id} data-fw-minimap-ref={ref.id}>
            <line
              x1={from.cx}
              y1={from.cy}
              x2={to.cx}
              y2={to.cy}
              stroke={color}
              strokeWidth={1.2}
              strokeDasharray="3 2"
              opacity={0.85}
              markerEnd={`url(#ref-arrow-${colorKey})`}
              markerStart={ref.bidirectional ? `url(#ref-arrow-${colorKey})` : undefined}
              style={{ pointerEvents: onRefClick ? 'auto' : 'none', cursor: onRefClick ? 'pointer' : 'default' }}
              onClick={onRefClick ? () => onRefClick(ref.id) : undefined}
            />
          </g>
        );
      })}
    </svg>
  );
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function computeBounds(
  groupBlocks: WorkflowBlock[],
  dimensions?: Record<string, BlockDimensions>,
): Bounds | null {
  if (groupBlocks.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const b of groupBlocks) {
    const w = dimensions?.[b.id]?.width ?? 200;
    const h = dimensions?.[b.id]?.height ?? 80;
    minX = Math.min(minX, b.x - w / 2);
    minY = Math.min(minY, b.y - h / 2);
    maxX = Math.max(maxX, b.x + w / 2);
    maxY = Math.max(maxY, b.y + h / 2);
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

function inViewport(p: DocRect, width: number, height: number): boolean {
  return p.cx >= 0 && p.cx <= width && p.cy >= 0 && p.cy <= height;
}
